import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Select } from '@wit-support/ui';
import { STATUS_LABEL_RU, StatusPill } from '@wit-support/ui-domain';
import { useConversationStatusMutation } from '../hooks/use-conversation-status-mutation.js';
import { StatusChangePopover } from './status-change-popover.js';
/**
 * The full conversation status enum (all 6 values, including `closed`) for the
 * operator status control. Ordered to match the lifecycle. Labels are RU to
 * match the rest of the operator surface.
 */
export const STATUS_OPTIONS = [
    'open',
    'pending',
    'waiting',
    'snoozed',
    'resolved',
    'closed',
];
/**
 * Conversation status control shown in the thread header. `canEdit` mirrors the
 * server's `conversation.reply` policy (the key `PATCH /:id/status` enforces):
 * holders get an interactive Select over all 6 status values, everyone else the
 * read-only pill — the same gate `ReplyComposer` sits behind. On change it
 * PATCHes the conversation and invalidates the conversation + list queries so
 * the header pill, the inbox row, and any status filter stay in sync.
 *
 * Выбор в `Select` НЕ отправляет запрос сам по себе: он лишь запоминает
 * НАМЕРЕНИЕ и открывает {@link StatusChangePopover}, где к смене можно
 * приложить комментарий. Именно поэтому `value` остаётся привязанным к
 * ФАКТИЧЕСКОМУ статусу — пока смена не подтверждена, контрол обязан показывать
 * то, что записано в базе, иначе отмена оставила бы в шапке чужой ярлык.
 * Разбор очереди без комментария остаётся двумя движениями: выбор → Enter.
 */
export function StatusControl({ conversationId, status, canEdit, }) {
    const [nextStatus, setNextStatus] = useState(null);
    // Был ли выбор значения в ТОЛЬКО ЧТО закрывшемся списке — см. `onCloseAutoFocus`.
    const justPicked = useRef(false);
    // Триггер `Select` — куда карточка подтверждения возвращает фокус, закрываясь.
    const triggerRef = useRef(null);
    // Подтверждение отправлено — после закрытия карточки фокус должен оказаться
    // на триггере. Возврат делается ЭФФЕКТОМ ниже, а не только `returnFocusTo`
    // карточки: `onChanged` вызывается из `onSuccess` мутации ДО того, как
    // TanStack разошлёт `success` наблюдателям, поэтому рендер, закрывающий
    // карточку, ещё видит `isPending === true` и триггер `disabled`. Radix
    // зовёт `onCloseAutoFocus` в своём `setTimeout(0)` после размонтирования —
    // `focus()` на заблокированной кнопке там no-op, а `preventDefault()` уже
    // подавил его собственный возврат: фокус уходил на `body`.
    const returnFocusAfterConfirm = useRef(false);
    // `onChanged` срабатывает только на успехе: на ошибке карточка остаётся
    // открытой вместе с набранным комментарием, и повтор не требует перенабора.
    const mut = useConversationStatusMutation(conversationId, () => setNextStatus(null));
    useEffect(() => {
        if (!returnFocusAfterConfirm.current)
            return;
        // Ждём ОБА условия: карточка закрыта И мутация завершилась — только тогда
        // триггер снова не `disabled`, и `focus()` действительно его берёт.
        if (nextStatus != null || mut.isPending)
            return;
        returnFocusAfterConfirm.current = false;
        triggerRef.current?.focus();
    }, [nextStatus, mut.isPending]);
    if (!canEdit) {
        return _jsx(StatusPill, { kind: "conversation", status: status, size: "sm" });
    }
    return (_jsx(StatusChangePopover, { open: nextStatus != null, 
        // Пока карточка открыта, `nextStatus` не null; `?? status` — только
        // затем, чтобы тип был непустым в закрытом состоянии.
        status: nextStatus ?? status, pending: mut.isPending, returnFocusTo: triggerRef, onCancel: () => {
            if (!mut.isPending)
                setNextStatus(null);
        }, onConfirm: (comment) => {
            if (nextStatus == null)
                return;
            returnFocusAfterConfirm.current = true;
            mut.mutate(comment != null ? { status: nextStatus, comment } : { status: nextStatus });
        }, children: _jsx("span", { className: "inline-flex", children: _jsxs(Select, { value: status, onValueChange: (v) => {
                    justPicked.current = true;
                    setNextStatus(v);
                }, disabled: mut.isPending, children: [_jsx(Select.Trigger, { ref: triggerRef, size: "sm", className: "w-auto min-w-[7.5rem]", "aria-label": "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u0442\u0430\u0442\u0443\u0441", title: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u0442\u0430\u0442\u0443\u0441 \u043F\u0435\u0440\u0435\u043F\u0438\u0441\u043A\u0438", children: _jsx(Select.Value, {}) }), _jsx(Select.Content
                    // ЗАКРЫВАЯСЬ ПОСЛЕ ВЫБОРА, `Select` НЕ ВОЗВРАЩАЕТ ФОКУС на триггер.
                    // Он делает это уже после того, как карточка подтверждения
                    // смонтирована, и её слой закрытия читает этот возврат как «фокус
                    // ушёл наружу»: карточка появлялась и пропадала в одном кадре — на
                    // экране не было ничего (проверено в браузере счётчиком мутаций
                    // DOM: added, затем removed). Юнит-тесты этого увидеть не могли —
                    // там Popover подменён прозрачной обёрткой без слоя закрытия.
                    //
                    // Гасим возврат фокуса ТОЛЬКО после выбора: при закрытии по Esc или
                    // клику мимо карточки нет, и фокус обязан вернуться на триггер,
                    // иначе клавиатура теряет место. Фокус в этом случае забирает сама
                    // карточка — на своё поле комментария.
                    , { 
                        // ЗАКРЫВАЯСЬ ПОСЛЕ ВЫБОРА, `Select` НЕ ВОЗВРАЩАЕТ ФОКУС на триггер.
                        // Он делает это уже после того, как карточка подтверждения
                        // смонтирована, и её слой закрытия читает этот возврат как «фокус
                        // ушёл наружу»: карточка появлялась и пропадала в одном кадре — на
                        // экране не было ничего (проверено в браузере счётчиком мутаций
                        // DOM: added, затем removed). Юнит-тесты этого увидеть не могли —
                        // там Popover подменён прозрачной обёрткой без слоя закрытия.
                        //
                        // Гасим возврат фокуса ТОЛЬКО после выбора: при закрытии по Esc или
                        // клику мимо карточки нет, и фокус обязан вернуться на триггер,
                        // иначе клавиатура теряет место. Фокус в этом случае забирает сама
                        // карточка — на своё поле комментария.
                        onCloseAutoFocus: (event) => {
                            if (!justPicked.current)
                                return;
                            justPicked.current = false;
                            event.preventDefault();
                        }, children: STATUS_OPTIONS.map((s) => (_jsx(Select.Item, { value: s, children: STATUS_LABEL_RU[s] }, s))) })] }) }) }));
}
//# sourceMappingURL=status-control.js.map